import { getLaminasApiData } from 'services/api';
import parse from 'html-react-parser';
import { useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { StoreContext } from 'models/import';
import TickIcon from 'assets/icons/bigTick.svg';
import TopPartnerIcon from 'assets/icons/topPartner.svg';
import { ProStatusLevel } from 'models/general';
import {
  DealDetailFE, DealDetailReponse, DealItem,
} from '../models/partner';
import Actions, { ActionModifier } from './actions';
import Btn, { BtnModifier } from './btn';
import CardBadge from './cardBadge';
import ExpiryLabel from './expiryLabel';
import IconList from './iconList';
import Modal from './modal';
import Pill, { PillModifier } from './pill';
import Skeleton from './skeleton';

export enum DealModifier {
  FEATURED = 'featured',
}

export interface DealCardProps {
  deal: DealItem;
  modifier?: DealModifier;
  isStatic?: boolean;
}

function DealCard(props: DealCardProps) {
  const { t } = useTranslation();
  const { userSettings, isProPage } = useContext(StoreContext);

  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isActive, setIsActive] = useState(false);
  const [dealDetail, setDealDetail] = useState<DealDetailFE | undefined>(undefined);
  const { deal } = props;
  const { partner } = deal;
  const aboutName = partner
    && partner.display_name
    && partner.top_partner
    && props.modifier === DealModifier.FEATURED ? ` ${partner.display_name}` : '';

  const actionModifiers = props.modifier === DealModifier.FEATURED
    ? [ActionModifier.RIGHT, ActionModifier.NO_MARGIN]
    : [ActionModifier.RIGHT, ActionModifier.NO_MARGIN, ActionModifier.NARROW];

  if (!deal) return null;

  const getDealDetail = (id: number) => getLaminasApiData<DealDetailReponse>(`/benefits/deal/detail?id=${id}`);

  const getNonPremiumModalData = (): DealDetailFE => {
    let title = '';
    let headline = '';
    let instructions = '';

    switch (userSettings.proStatus) {
      case null: // GUEST
        title = 'Login/Register';
        headline = 'You are not logged in yet. Please log in to access the deal.';
        instructions = 'Only logged-in users get access to all the exclusive deals and discounts from a variety of cryptocurrency services.';
        break;
      case ProStatusLevel.FREE:
        title = 'Premium Account';
        headline = "You don't have a Premium account yet, upgrade to access the deal";
        instructions = 'Only with your Premium subscription to CoinTracking, you get access to all the exclusive deals and discounts from a variety of cryptocurrency services.';
        break;
      case ProStatusLevel.PRO:
        title = 'Premium Account';
        headline = "You don't have a Premium account yet, upgrade to access the deal";
        instructions = 'Only with your Premium subscription to CoinTracking, you get access to all the exclusive deals and discounts from a variety of cryptocurrency services.';
        break;
      case ProStatusLevel.EXPERT_20K:
        title = 'Premium Account';
        headline = "You don't have a Premium account yet, upgrade to access the deal";
        instructions = 'Only with your Premium subscription to CoinTracking, you get access to all the exclusive deals and discounts from a variety of cryptocurrency services.';
        break;
      case ProStatusLevel.EXPERT_50K:
        title = 'Premium Account';
        headline = "You don't have a Premium account yet, upgrade to access the deal";
        instructions = 'Only with your Premium subscription to CoinTracking, you get access to all the exclusive deals and discounts from a variety of cryptocurrency services.';
        break;
      case ProStatusLevel.EXPERT_100K:
        title = 'Premium Account';
        headline = "You don't have a Premium account yet, upgrade to access the deal";
        instructions = 'Only with your Premium subscription to CoinTracking, you get access to all the exclusive deals and discounts from a variety of cryptocurrency services.';
        break;
      case ProStatusLevel.UNLIMITED:
        title = 'Premium Account';
        headline = "You don't have a Premium account yet, upgrade to access the deal";
        instructions = 'Only with your Premium subscription to CoinTracking, you get access to all the exclusive deals and discounts from a variety of cryptocurrency services.';
        break;
      default:
        break;
    }

    return {
      title: t(title, { ns: 'partner' }),
      headline: t(headline, { ns: 'partner' }),
      uri: '/pro.php',
      modalBtnText: t('Upgrade Now', { ns: 'partner' }),
      instructions: `<div>${t(instructions, { ns: 'partner' })}</div>`,
    };
  };

  const onRequestDetail = () => {
    setIsLoading(true);
    setIsActive(false);
    setShowModal(!showModal);

    if (
      deal.accessible_pro_status_ids
      && deal.accessible_pro_status_ids.length
      && !deal.accessible_pro_status_ids.includes(userSettings.proStatus)
    ) {
      setIsLoading(false);
      setDealDetail(getNonPremiumModalData());
    } else if (dealDetail) {
      // deal detail was already set for this card
      setIsLoading(false);
    } else {
      getDealDetail(props.deal.id)
        .then((res: DealDetailReponse) => {
          const { data: detail } = res;
          if (detail) {
            const hasPremium = !!(
              detail.accessible_pro_status_ids.includes(userSettings.proStatus)
              && detail.instructions
              && detail.uri
            );

            const modalData: DealDetailFE = {
              title: detail.partner.display_name || '',
              headline: detail.headline,
              uri: detail.uri || '',
              modalBtnText: 'Go to Partner website',
              instructions: detail.instructions || '',
            };

            const nonPremiumModalData = getNonPremiumModalData();

            setIsLoading(false);
            setDealDetail(hasPremium ? modalData : nonPremiumModalData);
          }
        }).catch((e) => {
          // eslint-disable-next-line no-console
          console.log(e);
          setIsLoading(true);
        });
    }
  };

  const onSubmit = (url: string) => {
    if (url && url.length) window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <div
      className={`ct_dealCard ${props.modifier ? `_${props.modifier}` : ''} ${props.isStatic ? '_static' : ''}`}
      onMouseEnter={() => setIsActive(true)}
      onMouseLeave={() => setIsActive(false)}
    >
      <div className="ct_dealCard-head">
        <div className="ct_dealCard-infos">
          {partner && partner.top_partner && <CardBadge icon={<TopPartnerIcon />} text={t('Top Partner', { ns: 'partner' })} />}
          {deal.expiry_date && <ExpiryLabel date={deal.expiry_date} />}
        </div>
        {partner && (
          <div className="ct_dealCard-logo">
            <img src={partner.logo_uri} alt="" />
          </div>
        )}
      </div>
      <div className="ct_dealCard-body">
        <div className="ct_dealCard-header">
          <div className="ct_dealCard-title">{partner && partner.display_name}</div>
          <div className="ct_dealCard-pills">
            {!!deal.tags && !!deal.tags.length
              && deal.tags.map((tag, index) => (
                <div className="ct_dealCard-pill" key={index}>
                  <Pill
                    value={tag.display_name}
                    modifiers={isActive
                      ? [PillModifier.NEUTRAL, PillModifier.HOVER]
                      : [PillModifier.NEUTRAL]}
                  />
                </div>
              ))}
          </div>
        </div>
        <div className="ct_dealCard-headline">{deal.headline}</div>
        <div className="ct_dealCard-content">
          {!props.modifier && !isProPage && (
            <div className="ct_dealCard-description">{parse(deal.description)}</div>
          )}
          {!!deal.selling_points && !!deal.selling_points.length && (
            <IconList icon={<TickIcon />} items={deal.selling_points} />
          )}
        </div>
        <div className="ct_dealCard-footer">
          <Actions modifiers={actionModifiers}>
            {!props.isStatic
              && partner && partner.top_partner
              && partner.name
              && props.modifier && props.modifier === DealModifier.FEATURED
              ? (
                <Btn
                  type="secondary"
                  text={`${t('About', { ns: 'partner' })}${aboutName}`}
                  modifier={[BtnModifier.SMALL]}
                  onClick={() => navigate(`/partners/${partner.name}`)}
                />
              )
              : <div />}
            <Btn
              type="primary"
              text={deal.accessible_pro_status_ids.includes(userSettings.proStatus) ? t('Go to deal', { ns: 'partner' }) : t('Premium only', { ns: 'partner' })}
              modifier={[BtnModifier.SMALL]}
              onClick={() => onRequestDetail()}
            />
          </Actions>

          <Modal
            showModal={showModal}
            title={dealDetail?.title || <Skeleton height="18px" width="150px" />}
            headline={dealDetail?.headline || <Skeleton height="60px" width="80%" />}
            btnText={dealDetail?.modalBtnText || ''}
            onModalClose={() => setShowModal(false)}
            onModalAction={() => onSubmit(dealDetail?.uri || '')}
            isLoading={isLoading}
          >
            <span>
              {!!dealDetail && !!dealDetail.instructions && parse(dealDetail.instructions)}
              {!dealDetail && Array.from(Array(2)).map((e, i) => <div className="ct_p" key={i}><Skeleton height="16px" /></div>)}
            </span>
          </Modal>
        </div>
      </div>
    </div>
  );
}

DealCard.defaultProps = { ...{} };

export default DealCard;
